import React, { type RefObject, useCallback, useEffect, useRef } from 'react';

type InfiniteScrollViewProps = {
	hasMore?: boolean;
	loadMore?: () => void;
	children: React.ReactNode;
	loader?: React.ReactNode;
};

/**
 * Component that provides a way to fetch more data as the user reaches the end/bottom of an element.
 */
export function InfiniteScrollView({
	hasMore,
	children,
	loader,
	loadMore,
}: InfiniteScrollViewProps): React.ReactNode {
	const loadMoreRef: RefObject<HTMLDivElement> = useRef(null);

	const fetchMoreCallback = useCallback(
		([loadMoreEntry]: IntersectionObserverEntry[]) => {
			// Automatically load more data when our invisible "load more" div is within the viewport
			if (loadMoreEntry.isIntersecting && hasMore) {
				loadMore?.();
			}
		},
		[loadMore, hasMore],
	);

	useEffect(() => {
		const intersectionObserver = new IntersectionObserver(fetchMoreCallback);

		if (loadMoreRef.current) {
			intersectionObserver.observe(loadMoreRef.current);
		}

		return () => {
			intersectionObserver.disconnect();
		};
	}, [loadMoreRef, fetchMoreCallback]);
	return (
		<>
			{children}
			<div ref={loadMoreRef} data-testid="infinite-scroll-ref" />
			{hasMore && loader}
		</>
	);
}
